<template>
	<div class="stepWorkMode">
		<header class="stepWorkMode__header">
			<h2 class="stepWorkMode__title">{{ $t('setupWizard_work_mode_title') }}</h2>
			<p class="stepWorkMode__caption">{{ $t('setupWizard_work_mode_caption') }}</p>
		</header>

		<WorkModeTileSelect
			:model-value="isWorkspaceMode"
			@update:model-value="selectMode"
		/>

		<div
			class="stepWorkMode__options"
			v-if="isWorkspaceMode"
		>
			<div class="stepWorkMode__row">
				<div class="stepWorkMode__rowIcon">
					<i :class="iconClass('plus')" />
				</div>
				<div class="stepWorkMode__rowText">
					<div class="stepWorkMode__rowTitle">
						{{ $t('workspace_tabs_create_actions_split_view_label') }}
					</div>
				</div>
				<ToggleSwitch
					:model-value="workspaceTabsCreateActionsInSplitView"
					@update:model-value="onCreateActionsInSplitViewChange"
				/>
			</div>

			<div class="stepWorkMode__row">
				<div class="stepWorkMode__rowIcon">
					<i :class="iconClass('notebook')" />
				</div>
				<div class="stepWorkMode__rowText">
					<div class="stepWorkMode__rowTitle">{{ $t('workspace_tabs_records_split_view_label') }}</div>
				</div>
				<ToggleSwitch
					:model-value="workspaceTabsRecordsInSplitView"
					@update:model-value="onRecordsInSplitViewChange"
				/>
			</div>
		</div>
	</div>
</template>

<script lang="ts">
import { computed, defineComponent, inject } from 'vue';

import ToggleSwitch from 'primevue/toggleswitch';
import { useWorkspaceModeSwitch } from 'src/composables/workspaceTabs/useWorkspaceModeSwitch';
import { useUserInterfaceSettingsStore } from 'src/stores/userInterfaceSettings';

import WorkModeTileSelect from 'src/components/workspace/WorkModeTileSelect.vue';

import { CommandBus } from 'src/util/command/CommandBus';

export default defineComponent({
	components: { ToggleSwitch, WorkModeTileSelect },
	name: 'StepWorkMode',
	setup() {
		const store = useUserInterfaceSettingsStore();
		const commandBus = inject<CommandBus | undefined>('commandBus', undefined);
		const { applyCreateActionsInSplitView, applyRecordsInSplitView, applyWorkspaceTabsToggle } =
			useWorkspaceModeSwitch(commandBus);

		const isWorkspaceMode = computed<boolean>(() => store.workspaceTabsEnabled);

		const useSolid = computed<boolean>(() => store.menuItemsSolid);

		function iconClass(name: string): string {
			return useSolid.value ? `fa-solid fa-${name}` : `fa-light fa-${name}`;
		}

		const workspaceTabsCreateActionsInSplitView = computed<boolean>(
			() => store.workspaceTabsCreateActionsInSplitView
		);

		const workspaceTabsRecordsInSplitView = computed<boolean>(() => store.workspaceTabsRecordsInSplitView);

		function selectMode(workspaceTabsEnabled: boolean): void {
			applyWorkspaceTabsToggle(workspaceTabsEnabled);
		}

		function onCreateActionsInSplitViewChange(value: boolean): void {
			applyCreateActionsInSplitView(value);
		}

		function onRecordsInSplitViewChange(value: boolean): void {
			applyRecordsInSplitView(value);
		}

		return {
			iconClass,
			isWorkspaceMode,
			onCreateActionsInSplitViewChange,
			onRecordsInSplitViewChange,
			selectMode,
			workspaceTabsCreateActionsInSplitView,
			workspaceTabsRecordsInSplitView,
		};
	},
});
</script>

<style lang="scss" scoped>
.stepWorkMode {
	display: flex;
	flex-direction: column;
	gap: 1rem;
}

.stepWorkMode__title {
	font-size: var(--text-xl);
	font-weight: 600;
	letter-spacing: -0.01em;
	margin: 0 0 0.125rem;
}

.stepWorkMode__caption {
	color: var(--p-text-muted-color);
	font-size: var(--text-sm);
	margin: 0;
}

.stepWorkMode__options {
	display: flex;
	flex-direction: column;
	gap: 0.5rem;
}

.stepWorkMode__row {
	align-items: center;
	background: var(--p-surface-0);
	border: 1px solid var(--p-surface-200);
	border-radius: 0.5rem;
	display: flex;
	gap: 0.875rem;
	padding: 0.625rem 1rem;
	transition: background-color 150ms ease;

	&:hover {
		background: var(--hover-light);
	}
}

.stepWorkMode__rowIcon {
	color: var(--p-primary-500);
	flex-shrink: 0;
	font-size: var(--text-lg);
	width: 1.5rem;
}

.stepWorkMode__rowText {
	flex: 1;
	min-width: 0;
}

.stepWorkMode__rowTitle {
	color: var(--p-text-color);
	font-size: var(--text-sm-plus);
	font-weight: 500;
}
</style>

<style lang="scss">
.body--dark .stepWorkMode__row {
	background: var(--p-surface-800);
	border-color: var(--p-surface-700);
}

.body--dark .stepWorkMode__row:hover {
	background: var(--hover-dark);
}
</style>
